import { getLogger } from '@otter-ai/telemetry';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import type { BuiltInApps } from '@/shared/types/apps';
import { getConnectedAppsQueryCreator } from '../query/getConnectedAppsQueryCreator';
import type {
  ConnectedApp,
  DropboxConnectedApp,
  GoogleConnectedApp,
  HubSpotConnectedApp,
  MicrosoftConnectedApp,
  SalesforceConnectedApp,
  SlackConnectedApp,
  ZoomConnectedApp,
} from '../type/ConnectedApps';

export type UseConnectedApps<A> = {
  connectedApps: A[];
  isLoading: boolean;
  isPending: boolean;
  refetch: () => Promise<A[]>;
};

const logger = getLogger('settings.integrations.use-connected-apps');

/**
 * Gets the configurations and data for third party apps (Zoom, Slack, Salesforce, etc.)
 * that are connected to the current user's account.
 *
 * When filtering by service, an array of configurations is always returned.
 * The Recall API allows some services to connect multiple accounts (Google and Microsoft),
 * but only a single account for most other services.
 *
 * @example
 * Get all the state for all connected apps
 * ```tsx
 * const { connectedApps, isLoading, isPending } = useConnectedApps();
 * ```
 *
 * @example
 * Get all the Zoom connections state
 * ```tsx
 * const { connectedApps, isLoading, isPending } = useConnectedApps({
 *   service: BuiltInApps.ZOOM,
 * });
 *
 * return <div>{connectedApps.map((app) => <div key={app.id}>{app.name}</div>)}</div>;
 * ```
 */
export function useConnectedApps(args: {
  service: 'Zoom';
}): UseConnectedApps<ZoomConnectedApp>;
export function useConnectedApps(args: {
  service: 'HubSpot';
}): UseConnectedApps<HubSpotConnectedApp>;
export function useConnectedApps(args: {
  service: 'Salesforce';
}): UseConnectedApps<SalesforceConnectedApp>;
export function useConnectedApps(args: {
  service: 'Slack';
}): UseConnectedApps<SlackConnectedApp>;
export function useConnectedApps(args: {
  service: 'DropBox';
}): UseConnectedApps<DropboxConnectedApp>;
export function useConnectedApps(args: {
  service: 'Google';
}): UseConnectedApps<GoogleConnectedApp>;
export function useConnectedApps(args: {
  service: 'Microsoft';
}): UseConnectedApps<MicrosoftConnectedApp>;
export function useConnectedApps({
  service,
}: {
  service?: BuiltInApps;
}): UseConnectedApps<ConnectedApp> {
  const {
    data: connectedApps,
    isLoading,
    isPending,
    refetch: refetchQuery,
  } = useQuery(getConnectedAppsQueryCreator());

  const refetch = useCallback(async () => {
    const { data, isSuccess, error } = await refetchQuery();
    if (!isSuccess || !data) {
      logger.error({
        eventName: 'Failed_Refetch',
        error,
        attributes: {
          Service: service ?? 'All',
        },
      });
      throw error;
    }
    return filterConnectedApps(data, service);
  }, [refetchQuery, service]);

  return useMemo(
    () => ({
      connectedApps: filterConnectedApps(connectedApps, service),
      isLoading,
      isPending,
      refetch,
    }),
    [connectedApps, service, isLoading, isPending],
  );
}

function filterConnectedApps(
  connectedApps: ConnectedApp[] | undefined,
  service: BuiltInApps | undefined,
) {
  if (!connectedApps) {
    return [];
  }

  if (service) {
    return connectedApps.filter((app) => app.source === service);
  }

  return connectedApps;
}
